import JSZip from "jszip";
import {
  ArrowDownToLine,
  ArrowUp,
  ArrowDown,
  CheckCircle2,
  FileJson,
  Images,
  LockKeyhole,
  PackageOpen,
  RotateCcw,
  Settings2,
  Trash2,
  Upload,
  WandSparkles,
  History,
  X,
  createIcons,
} from "lucide";
import { availableLayouts, calculateLayout, LAYOUTS, recommendedLayout, scaledCrop } from "./geometry.js";
import { decodeImageFile, FORMAT_LABEL, IMAGE_ACCEPT, isSupportedImage } from "./image-decoder.js";
import { CREDENTIAL_MIN_WIDTH, CREDENTIAL_STRIP_HEIGHT, drawCredentialStrip, readCredentialFromBitmap } from "./embedded-credential.js";
import { deleteTask, listTasks, saveTask } from "./task-store.js";
import "./styles.css";

const icons = {
  ArrowDownToLine,
  ArrowUp,
  ArrowDown,
  CheckCircle2,
  FileJson,
  Images,
  LockKeyhole,
  PackageOpen,
  RotateCcw,
  Settings2,
  Trash2,
  Upload,
  WandSparkles,
  History,
  X,
};

const state = {
  mode: "compose",
  count: 4,
  images: [],
  queue: [],
  layout: "grid",
  gap: 32,
  background: "#f4f4f2",
  generated: null,
  generatedBatch: [],
  restoreImage: null,
  manifest: null,
  restoreItems: [],
  restoreResults: [],
  allowResize: false,
  importStatus: "",
  progress: null,
  historyOpen: false,
  tasks: [],
  taskQuery: "",
};

document.querySelector("#app").innerHTML = `
  <div class="app-shell">
    <header class="topbar">
      <div class="brand"><span class="brand-mark"><i></i><i></i><i></i><i></i></span>照片拼合还原</div>
      <div class="topbar-status"><div class="privacy"><i data-lucide="lock-keyhole"></i><span>照片只在本机浏览器中处理</span></div></div>
    </header>
    <main>
      <section class="intro">
        <div>
          <h1>拼在一起，准确还原</h1>
          <p>不缩放原图，修图后按原始坐标和分辨率拆回单张。</p>
        </div>
        <div class="tabs" role="tablist">
          <button class="tab active" data-mode="compose" role="tab">合成照片</button>
          <button class="tab" data-mode="restore" role="tab">还原单张</button>
        </div>
      </section>
      <section id="workspace" class="workspace"></section>
      <p class="footer-note">照片不会上传；还原坐标会保存在合成图内和当前浏览器任务库中。</p>
    </main>
  </div>
`;

const workspace = document.querySelector("#workspace");

document.querySelectorAll("[data-mode]").forEach((button) => {
  button.addEventListener("click", () => {
    state.mode = button.dataset.mode;
    document.querySelectorAll("[data-mode]").forEach((item) => item.classList.toggle("active", item === button));
    render();
  });
});

function render() {
  workspace.innerHTML = `${state.historyOpen ? taskHistoryView() : ""}${state.mode === "compose" ? composeView() : restoreView()}`;
  createIcons({ icons });
  state.mode === "compose" ? bindCompose() : bindRestore();
  bindHistory();
}

function taskHistoryView() {
  return `<section class="task-history" aria-label="本机任务历史">
    <div class="history-head"><div><h2 class="section-title"><i data-lucide="history"></i>本机任务库</h2><p class="hint">仅保存还原坐标，不保存原始照片。</p></div><button id="close-history" class="icon-btn" title="关闭任务库"><i data-lucide="x"></i></button></div>
    <div class="history-toolbar"><input id="task-query" value="${escapeHtml(state.taskQuery)}" placeholder="搜索任务编号、尺寸或文件名" aria-label="搜索任务"><button id="refresh-history" class="secondary">刷新</button></div>
    <div class="history-list">${state.tasks.length ? state.tasks.map((task) => `<div class="history-item"><div><strong>${escapeHtml(task.taskId)}</strong><span>${task.imageCount} 张 · ${task.canvasSize} · ${new Date(task.createdAt).toLocaleString()}</span></div><div class="history-actions"><button data-use-task="${escapeHtml(task.taskId)}" class="secondary">使用</button><button data-export-task="${escapeHtml(task.taskId)}" class="icon-btn" title="导出备用凭证"><i data-lucide="file-json"></i></button><button data-delete-task="${escapeHtml(task.taskId)}" class="icon-btn" title="删除任务"><i data-lucide="trash-2"></i></button></div></div>`).join("") : `<p class="hint">暂无本机任务记录。</p>`}</div>
  </section>`;
}

function bindHistory() {
  workspace.querySelector("#close-history")?.addEventListener("click", () => { state.historyOpen = false; render(); });
  workspace.querySelector("#refresh-history")?.addEventListener("click", loadTasks);
  workspace.querySelector("#task-query")?.addEventListener("change", (event) => { state.taskQuery = event.target.value; loadTasks(); });
  workspace.querySelectorAll("[data-use-task]").forEach((button) => button.addEventListener("click", () => {
    const task = state.tasks.find((item) => item.taskId === button.dataset.useTask);
    if (!task) return;
    state.manifest = { ...task.manifest, fileName: "从本机任务库选择" }; state.mode = "restore"; state.historyOpen = false; render();
  }));
  workspace.querySelectorAll("[data-delete-task]").forEach((button) => button.addEventListener("click", async () => { await deleteTask(button.dataset.deleteTask); await loadTasks(); }));
  workspace.querySelectorAll("[data-export-task]").forEach((button) => button.addEventListener("click", () => {
    const task = state.tasks.find((item) => item.taskId === button.dataset.exportTask);
    if (task) downloadBlob(new Blob([JSON.stringify(task.manifest, null, 2)], { type: "application/json" }), `${task.taskId}-还原凭证.json`);
  }));
}

async function loadTasks() {
  state.tasks = await listTasks(state.taskQuery);
  state.historyOpen = true;
  render();
}

function composeView() {
  const ready = state.images.length === state.count;
  const total = state.images.length + state.queue.length;
  const completeGroups = Math.floor(total / state.count);
  const remainder = total % state.count;
  const geometry = ready ? calculateLayout(state.images, state.layout, state.gap) : null;
  const outputSize = geometry ? { width: Math.max(geometry.width, CREDENTIAL_MIN_WIDTH), height: geometry.height + CREDENTIAL_STRIP_HEIGHT } : null;
  return `
    <div class="stage">
      <h2 class="section-title"><i data-lucide="images"></i>原始照片</h2>
      ${state.images.length ? photoList() : ""}
      ${state.images.length < state.count ? dropzone("compose", `添加 ${state.count - state.images.length} 张照片`, `支持 ${FORMAT_LABEL}；苹果 HEIC 会在本机解码`) : ""}
      ${state.queue.length ? batchQueuePanel(completeGroups, remainder) : ""}
      ${state.importStatus ? `<div class="success">${escapeHtml(state.importStatus)}</div>` : ""}
      ${state.generated ? generatedPanel() : ""}
      ${state.generatedBatch.length ? generatedBatchPanel() : ""}
    </div>
    <aside class="controls">
      <h2 class="section-title"><i data-lucide="settings-2"></i>合成设置</h2>
      <div class="field">
        <span class="field-label">照片数量</span>
        <div class="segmented">
          <button data-count="2" class="${state.count === 2 ? "active" : ""}">2 张</button>
          <button data-count="4" class="${state.count === 4 ? "active" : ""}">4 张</button>
        </div>
      </div>
      <div class="field">
        <span class="field-label">排列方式</span>
        <div class="segmented">
          ${availableLayouts(state.count).map((key) => `<button data-layout="${key}" class="${state.layout === key ? "active" : ""}">${LAYOUTS[key].label}</button>`).join("")}
        </div>
      </div>
      <div class="field">
        <label for="gap">照片间隔</label>
        <div class="range-row">
          <input id="gap-range" type="range" min="0" max="128" step="1" value="${state.gap}">
          <input id="gap" type="number" min="0" max="512" step="1" value="${state.gap}" aria-label="间隔像素">
        </div>
        <p class="hint">建议人像保留 32px，减少 AI 修图对相邻照片的干扰。</p>
      </div>
      <div class="field">
        <label for="background">空白区域颜色</label>
        <div class="range-row">
          <input id="background" type="color" value="${state.background}" aria-label="背景颜色">
          <input value="${state.background.toUpperCase()}" disabled aria-label="背景颜色值">
        </div>
      </div>
      <div class="summary">
        <div class="summary-row"><span>已添加</span><strong>${total} 张</strong></div>
        <div class="summary-row"><span>完整任务</span><strong>${completeGroups} 组</strong></div>
        <div class="summary-row"><span>合成分辨率</span><strong>${outputSize ? `${outputSize.width} × ${outputSize.height} px` : "等待照片"}</strong></div>
        <div class="summary-row"><span>图片凭证</span><strong>底部自动携带</strong></div>
        <div class="summary-row"><span>原图缩放</span><strong>0%</strong></div>
        ${geometry && (geometry.width > 16384 || geometry.height > 16384) ? `<div class="warning">合成图单边超过 16384px，部分浏览器或修图软件可能无法处理。请调整排列方式。</div>` : ""}
      </div>
      <button id="generate" class="primary accent full" ${ready ? "" : "disabled"}><i data-lucide="wand-sparkles"></i>生成自带凭证合成图</button>
      ${completeGroups > 1 ? `<button id="generate-batch" class="secondary full" style="margin-top:9px"><i data-lucide="package-open"></i>批量生成 ${completeGroups} 组</button>` : ""}
      ${state.images.length ? `<button id="clear" class="secondary full" style="margin-top:9px"><i data-lucide="trash-2"></i>清空照片</button>` : ""}
      <button id="open-history" class="secondary full" style="margin-top:9px"><i data-lucide="history"></i>打开本机任务库</button>
    </aside>
  `;
}

function batchQueuePanel(completeGroups, remainder) {
  return `
    <div class="batch-panel">
      <div><strong>批量工作区</strong><span>当前显示第 1 组，后面还有 ${state.queue.length} 张</span></div>
      <div class="batch-stats"><span>${completeGroups} 组可生成</span>${remainder ? `<span>${remainder} 张待补齐</span>` : ""}</div>
    </div>
  `;
}

function photoList() {
  return `<div class="photo-list">${state.images.map((image, index) => `
    <article class="photo-item">
      <img src="${image.url}" alt="${escapeHtml(image.name)}">
      <div class="photo-actions">
        <button class="icon-btn" data-up="${index}" title="向前移动" ${index === 0 ? "disabled" : ""}><i data-lucide="arrow-up"></i></button>
        <button class="icon-btn" data-down="${index}" title="向后移动" ${index === state.images.length - 1 ? "disabled" : ""}><i data-lucide="arrow-down"></i></button>
        <button class="icon-btn" data-remove="${index}" title="移除"><i data-lucide="x"></i></button>
      </div>
      <div class="format-badge">${escapeHtml(image.format)}</div>
      <div class="photo-meta"><div class="photo-name">${index + 1}. ${escapeHtml(image.name)}</div><div class="photo-size">${image.width} × ${image.height} px · ${escapeHtml(image.decoder)}</div></div>
    </article>`).join("")}</div>`;
}

function generatedPanel() {
  const data = state.generated;
  return `
    <div class="success">合成图已生成并写入还原凭证。修图后只需上传这一张图，底部凭证区请勿裁剪。</div>
    <div class="file-box"><strong>${escapeHtml(data.imageName)}</strong><span>${data.width} × ${data.height} px · PNG 无损 · 内含 ${data.credentialCopies} 份高纠错凭证</span></div>
    <button id="download-image" class="primary accent full" style="margin-top:12px"><i data-lucide="arrow-down-to-line"></i>下载合成图</button>
    <details class="advanced">
      <summary>备用与兼容选项</summary>
      <div class="advanced-actions">
        <button id="download-manifest" class="secondary"><i data-lucide="file-json"></i>JSON 备用凭证</button>
        <button id="download-package" class="secondary"><i data-lucide="package-open"></i>完整 ZIP</button>
      </div>
    </details>
  `;
}

function generatedBatchPanel() {
  return `
    <div class="success">${state.generatedBatch.length} 张合成图已生成。可以单独下载，也可以一次打包下载。</div>
    <div class="result-list">${state.generatedBatch.map((task, index) => `
      <div class="result-item"><div><strong>${escapeHtml(task.imageName)}</strong><span>第 ${index + 1} 组 · ${task.width} × ${task.height} px · ${task.manifest.images.length} 张原图</span></div><button class="secondary" data-download-composite="${index}"><i data-lucide="arrow-down-to-line"></i>单独下载</button></div>
    `).join("")}</div>
    <button id="download-all-composites" class="primary accent full" style="margin-top:12px"><i data-lucide="package-open"></i>全部打包下载 ZIP</button>
  `;
}

function restoreView() {
  const validation = validateRestore();
  const batchMode = state.restoreItems.length > 0;
  const readyItems = state.restoreItems.filter((item) => validateRestoreItem(item).ready);
  return `
    <div class="stage">
      <h2 class="section-title"><i data-lucide="rotate-ccw"></i>修完的合成图</h2>
      ${batchMode ? restoreBatchList() : state.restoreImage ? `
        <div class="photo-list" style="grid-template-columns:1fr">
          <article class="photo-item"><img src="${state.restoreImage.url}" alt="修完的合成图"><div class="photo-meta"><div class="photo-name">${escapeHtml(state.restoreImage.name)}</div><div class="photo-size">${state.restoreImage.width} × ${state.restoreImage.height} px</div></div></article>
        </div>` : dropzone("restore-image", "上传修完的合成图", `支持 ${FORMAT_LABEL}；应使用完整合成图`) }
      ${batchMode ? "" : `<h2 class="section-title" style="margin-top:20px"><i data-lucide="history"></i>凭证识别</h2>
      ${state.manifest ? `<div class="file-box"><strong>${escapeHtml(state.manifest.fileName)}</strong><span>${state.manifest.images.length} 张照片 · 目标 ${state.manifest.canvas.width} × ${state.manifest.canvas.height} px</span></div>` : `<div class="warning">未能从图片或本机任务库识别凭证。</div>${dropzone("manifest", "选择备用 JSON 凭证", "仅旧版图片或底部凭证区损坏时需要")}`}
      ${validation.html}`}
      ${state.restoreResults.length ? restoreResultsPanel() : ""}
    </div>
    <aside class="controls">
      <h2 class="section-title"><i data-lucide="settings-2"></i>还原检查</h2>
      <div class="summary">
        <div class="summary-row"><span>合成图</span><strong>${batchMode ? `${state.restoreItems.length} 张` : state.restoreImage ? `${state.restoreImage.width} × ${state.restoreImage.height} px` : "未上传"}</strong></div>
        <div class="summary-row"><span>凭证状态</span><strong>${batchMode ? `${readyItems.length} / ${state.restoreItems.length} 可还原` : state.manifest ? escapeHtml(state.manifest.fileName) : "等待识别"}</strong></div>
        <div class="summary-row"><span>待还原</span><strong>${batchMode ? `${readyItems.reduce((total, item) => total + item.manifest.images.length, 0)} 张原图` : state.manifest ? `${state.manifest.images.length} 张` : "-"}</strong></div>
      </div>
      ${batchMode && state.restoreItems.some((item) => item.manifest && item.image.width / item.manifest.canvas.width === item.image.height / item.manifest.canvas.height && item.image.width !== item.manifest.canvas.width) ? `<label class="check-row"><input id="allow-resize" type="checkbox" ${state.allowResize ? "checked" : ""}><span>允许等比例缩放的合成图应急还原。</span></label>` : ""}
      ${validation.mismatch ? `
        <label class="check-row"><input id="allow-resize" type="checkbox" ${state.allowResize ? "checked" : ""}><span>允许应急缩放还原。我知道这会重新采样像素，不能保证原始画质。</span></label>
      ` : ""}
      <button id="restore" class="primary accent full" ${batchMode ? (readyItems.length ? "" : "disabled") : (validation.ready ? "" : "disabled")}><i data-lucide="package-open"></i>${batchMode ? `还原 ${readyItems.length} 张合成图` : "拆分并下载 ZIP"}</button>
      ${(batchMode || state.restoreImage || state.manifest) ? `<button id="clear-restore" class="secondary full" style="margin-top:9px"><i data-lucide="trash-2"></i>清空文件</button>` : ""}
      <button id="open-history" class="secondary full" style="margin-top:9px"><i data-lucide="history"></i>打开本机任务库</button>
    </aside>
  `;
}

function restoreBatchList() {
  return `<div class="result-list">${state.restoreItems.map((item, index) => {
    const check = validateRestoreItem(item);
    return `<div class="result-item"><div><strong>${escapeHtml(item.image.name)}</strong><span>${item.image.width} × ${item.image.height} px · ${item.manifest ? `${item.manifest.images.length} 张原图` : "未识别凭证"}</span><small class="${check.ready ? "status-ok" : "status-error"}">${escapeHtml(check.message)}</small></div><button class="icon-btn" data-remove-restore="${index}" title="移除"><i data-lucide="x"></i></button></div>`;
  }).join("")}</div>`;
}

function restoreResultsPanel() {
  return `
    <h2 class="section-title" style="margin-top:20px"><i data-lucide="check-circle-2"></i>还原结果</h2>
    <div class="success">已还原 ${state.restoreResults.reduce((sum, result) => sum + result.files.length, 0)} 张照片。可逐张下载或全部打包下载。</div>
    <div class="result-groups">${state.restoreResults.map((result, groupIndex) => `
      <section class="result-group"><strong>${escapeHtml(result.taskId)}</strong><span>${result.files.length} 张</span>
        <div class="result-file-actions">${result.files.map((file, fileIndex) => `<button class="secondary" data-download-restored="${groupIndex}:${fileIndex}"><i data-lucide="arrow-down-to-line"></i>${escapeHtml(file.name)}</button>`).join("")}</div>
      </section>`).join("")}</div>
    <button id="download-all-restored" class="primary accent full" style="margin-top:12px"><i data-lucide="package-open"></i>全部打包下载 ZIP</button>
  `;
}

function dropzone(kind, title, note) {
  const accept = kind === "manifest" ? ".json,application/json" : IMAGE_ACCEPT;
  const multiple = kind === "compose" || kind === "restore-image" ? "multiple" : "";
  return `
    <div class="dropzone ${kind === "manifest" ? "compact" : ""}" data-drop="${kind}">
      <div><div class="drop-icon"><i data-lucide="upload"></i></div><h2>${title}</h2><p>${note}</p>
      <button class="primary" data-pick="${kind}"><i data-lucide="upload"></i>选择文件</button>
      <input class="hidden" data-input="${kind}" type="file" accept="${accept}" ${multiple}></div>
    </div>`;
}

function bindCompose() {
  bindDrops();
  workspace.querySelectorAll("[data-count]").forEach((button) => button.addEventListener("click", () => {
    const next = Number(button.dataset.count);
    const all = [...state.images, ...state.queue];
    state.count = next;
    state.images = all.slice(0, next);
    state.queue = all.slice(next);
    if (!availableLayouts(next).includes(state.layout)) state.layout = next === 4 ? "grid" : "horizontal";
    state.generated = null;
    render();
  }));
  workspace.querySelectorAll("[data-layout]").forEach((button) => button.addEventListener("click", () => {
    state.layout = button.dataset.layout;
    state.generated = null;
    render();
  }));
  bindNumber("gap", (value) => { state.gap = clamp(Math.round(value), 0, 512); state.generated = null; render(); });
  const range = workspace.querySelector("#gap-range");
  if (range) range.addEventListener("input", () => {
    state.gap = Number(range.value);
    const number = workspace.querySelector("#gap");
    if (number) number.value = range.value;
  });
  if (range) range.addEventListener("change", () => { state.generated = null; render(); });
  const background = workspace.querySelector("#background");
  if (background) background.addEventListener("change", () => { state.background = background.value; state.generated = null; render(); });
  workspace.querySelectorAll("[data-remove]").forEach((button) => button.addEventListener("click", () => {
    cleanupImage(state.images.splice(Number(button.dataset.remove), 1)[0]);
    if (state.queue.length) state.images.push(state.queue.shift());
    state.generated = null;
    render();
  }));
  workspace.querySelectorAll("[data-up]").forEach((button) => button.addEventListener("click", () => moveImage(Number(button.dataset.up), -1)));
  workspace.querySelectorAll("[data-down]").forEach((button) => button.addEventListener("click", () => moveImage(Number(button.dataset.down), 1)));
  workspace.querySelector("#generate")?.addEventListener("click", generatePackage);
  workspace.querySelector("#generate-batch")?.addEventListener("click", generateBatchPackage);
  workspace.querySelector("#clear")?.addEventListener("click", clearCompose);
  workspace.querySelector("#download-image")?.addEventListener("click", () => downloadBlob(state.generated.imageBlob, state.generated.imageName));
  workspace.querySelector("#download-manifest")?.addEventListener("click", () => downloadBlob(state.generated.manifestBlob, state.generated.manifestName));
  workspace.querySelector("#download-package")?.addEventListener("click", downloadFullPackage);
  workspace.querySelectorAll("[data-download-composite]").forEach((button) => button.addEventListener("click", () => {
    const task = state.generatedBatch[Number(button.dataset.downloadComposite)];
    if (task) downloadBlob(task.imageBlob, task.imageName);
  }));
  workspace.querySelector("#download-all-composites")?.addEventListener("click", downloadAllComposites);
  workspace.querySelector("#open-history")?.addEventListener("click", loadTasks);
}

function bindRestore() {
  bindDrops();
  workspace.querySelector("#allow-resize")?.addEventListener("change", (event) => { state.allowResize = event.target.checked; render(); });
  workspace.querySelector("#restore")?.addEventListener("click", restoreImages);
  workspace.querySelector("#clear-restore")?.addEventListener("click", clearRestore);
  workspace.querySelectorAll("[data-remove-restore]").forEach((button) => button.addEventListener("click", () => {
    const item = state.restoreItems.splice(Number(button.dataset.removeRestore), 1)[0];
    cleanupImage(item?.image);
    state.restoreResults = [];
    render();
  }));
  workspace.querySelectorAll("[data-download-restored]").forEach((button) => button.addEventListener("click", () => {
    const [groupIndex, fileIndex] = button.dataset.downloadRestored.split(":").map(Number);
    const file = state.restoreResults[groupIndex]?.files[fileIndex];
    if (file) downloadBlob(file.blob, file.name);
  }));
  workspace.querySelector("#download-all-restored")?.addEventListener("click", downloadAllRestored);
  workspace.querySelector("#open-history")?.addEventListener("click", loadTasks);
}

function bindDrops() {
  workspace.querySelectorAll("[data-pick]").forEach((button) => button.addEventListener("click", () => workspace.querySelector(`[data-input="${button.dataset.pick}"]`).click()));
  workspace.querySelectorAll("[data-input]").forEach((input) => input.addEventListener("change", () => handleFiles(input.dataset.input, [...input.files])));
  workspace.querySelectorAll("[data-drop]").forEach((zone) => {
    zone.addEventListener("dragover", (event) => { event.preventDefault(); zone.classList.add("dragover"); });
    zone.addEventListener("dragleave", () => zone.classList.remove("dragover"));
    zone.addEventListener("drop", (event) => {
      event.preventDefault(); zone.classList.remove("dragover"); handleFiles(zone.dataset.drop, [...event.dataTransfer.files]);
    });
  });
}

async function handleFiles(kind, files) {
  if (!files.length) return;
  setBusy(true);
  try {
    if (kind === "manifest") {
      state.manifest = parseManifest(JSON.parse(await files[0].text()), files[0].name);
    } else if (kind === "compose") {
      const supported = files.filter(isSupportedImage);
      const capacity = Math.max(0, 40 - state.images.length - state.queue.length);
      const candidates = supported.slice(0, capacity);
      if (!candidates.length) throw new Error(`请选择支持的图片：${FORMAT_LABEL}。`);
      const decoded = [];
      startProgress("正在读取照片", candidates.length);
      for (const [index, file] of candidates.entries()) {
        decoded.push(await decodeImageFile(file));
        updateProgress(index + 1, candidates.length, file.name);
      }
      const all = [...state.images, ...state.queue, ...decoded];
      state.images = all.slice(0, state.count);
      state.queue = all.slice(state.count);
      if (state.images.length === state.count) state.layout = recommendedLayout(state.images);
      state.generated = null;
      const ignored = files.length - candidates.length;
      const converted = decoded.filter((image) => image.decoder !== "浏览器原生").length;
      state.importStatus = `${decoded.length} 张照片已读取${converted ? `，其中 ${converted} 张已在本机转换` : ""}${ignored ? `；另有 ${ignored} 个文件因格式或数量限制被忽略` : ""}。`;
    } else {
      const supported = files.filter(isSupportedImage);
      if (!supported.length) throw new Error(`请选择支持的图片：${FORMAT_LABEL}。`);
      const decoded = [];
      startProgress("正在读取合成图", supported.length);
      for (const [index, file] of supported.entries()) {
        const image = await decodeImageFile(file);
        const manifest = await readCredentialFromBitmap(image.bitmap) || await findStoredManifest(image.width, image.height, image.name);
        decoded.push({ image, manifest });
        updateProgress(index + 1, supported.length, file.name);
      }
      cleanupImage(state.restoreImage);
      state.restoreItems.forEach((item) => cleanupImage(item.image));
      state.restoreResults = [];
      if (decoded.length === 1) {
        state.restoreImage = decoded[0].image;
        state.manifest = decoded[0].manifest;
        state.restoreItems = [];
      } else {
        state.restoreImage = null;
        state.manifest = null;
        state.restoreItems = decoded;
      }
    }
    setBusy(false);
    finishProgress();
    render();
  } catch (error) {
    alert(error.message || "文件读取失败。");
    setBusy(false);
    finishProgress();
  }
}

function parseManifest(value, fileName) {
  if (!["pixel-cake-stitcher/v1", "pixel-cake-stitcher/v3"].includes(value?.schema) || !value.canvas || !Array.isArray(value.images) || ![2, 4].includes(value.images.length)) {
    throw new Error("这不是本工具生成的有效还原凭证。");
  }
  if (!Number.isInteger(value.canvas.width) || !Number.isInteger(value.canvas.height)) throw new Error("凭证中的画布尺寸无效。");
  value.images.forEach((image) => {
    const values = [image.x, image.y, image.width, image.height];
    if (!values.every((item) => Number.isInteger(item) && item >= 0)) throw new Error("凭证中的切割坐标无效。");
    if (image.x + image.width > value.canvas.width || image.y + image.height > value.canvas.height) throw new Error("凭证中的切割区域超出画布。");
  });
  return { ...value, fileName };
}

async function findStoredManifest(width, height, fileName = "") {
  const matches = [];
  const taskId = fileName.match(/pc-\d{14}(?:-G\d{2})?/)?.[0];
  const indexedTasks = await listTasks();
  indexedTasks.forEach((task) => {
    if (task.manifest?.canvas?.width === width && task.manifest?.canvas?.height === height) matches.push(task.manifest);
  });
  for (let index = 0; index < localStorage.length; index += 1) {
    const key = localStorage.key(index);
    if (!key?.startsWith("pixel-cake-stitcher:")) continue;
    try {
      const manifest = JSON.parse(localStorage.getItem(key));
      if (manifest?.canvas?.width === width && manifest?.canvas?.height === height && !matches.some((item) => item.taskId === manifest.taskId)) matches.push(manifest);
    } catch {
      // Ignore invalid or stale browser records.
    }
  }
  const exactTask = taskId && matches.find((manifest) => manifest.taskId === taskId);
  if (exactTask) return { ...exactTask, fileName: "按文件名从本机任务库匹配" };
  if (matches.length !== 1) return null;
  return { ...matches[0], fileName: "按尺寸从本机任务库匹配" };
}

async function generatePackage() {
  setBusy(true);
  startProgress("正在生成合成图和图片凭证", 1);
  try {
    state.generated = await createTaskPackage(state.images);
    updateProgress(1, 1, "合成图已完成");
    await saveTask(state.generated.manifest);
    localStorage.setItem(`pixel-cake-stitcher:${state.generated.manifest.taskId}`, JSON.stringify(state.generated.manifest));
    setBusy(false);
    finishProgress();
    render();
  } catch (error) {
    alert(error.message || "合成失败。图片可能超过了当前浏览器的画布限制。");
    setBusy(false);
    finishProgress();
  }
}

async function createTaskPackage(images, suffix = "") {
  const geometry = calculateLayout(images, state.layout, state.gap);
  const canvasWidth = Math.max(geometry.width, CREDENTIAL_MIN_WIDTH);
  const photoOffsetX = Math.round((canvasWidth - geometry.width) / 2);
  const canvasHeight = geometry.height + CREDENTIAL_STRIP_HEIGHT;
  const canvas = createCanvas(canvasWidth, canvasHeight);
  const context = canvas.getContext("2d", { alpha: false });
  context.fillStyle = state.background;
  context.fillRect(0, 0, canvas.width, canvas.height);
  geometry.placements.forEach((placement, index) => {
    context.drawImage(images[index].bitmap, placement.x + photoOffsetX, placement.y, placement.width, placement.height);
  });
  const stamp = new Date().toISOString().replace(/[-:TZ.]/g, "").slice(0, 14);
  const taskId = `pc-${stamp}${suffix}`;
  const imageName = `${taskId}-合成图.png`;
  const manifestName = `${taskId}-还原凭证.json`;
  const manifest = {
    schema: "pixel-cake-stitcher/v3",
    createdAt: new Date().toISOString(),
    taskId,
    canvas: { width: canvasWidth, height: canvasHeight, background: state.background },
    photoArea: { width: canvasWidth, height: geometry.height },
    credential: { type: "qr-strip", height: CREDENTIAL_STRIP_HEIGHT },
    layout: state.layout,
    gap: state.gap,
    compositeFileName: imageName,
    images: images.map((image, index) => ({
      index,
      originalName: image.name,
      sourceFormat: image.format,
      outputName: outputName(image.name, index),
      ...geometry.placements[index],
      x: geometry.placements[index].x + photoOffsetX,
    })),
  };
  const credentialLength = await drawCredentialStrip(context, manifest);
  const imageBlob = await canvasBlob(canvas, "image/png");
  const manifestBlob = new Blob([JSON.stringify(manifest, null, 2)], { type: "application/json" });
  return {
    imageBlob, manifestBlob, manifest, imageName, manifestName,
    width: canvasWidth, height: canvasHeight,
    credentialCopies: canvasWidth >= 980 ? 2 : 1,
    credentialLength,
  };
}

async function generateBatchPackage() {
  setBusy(true);
  try {
    const all = [...state.images, ...state.queue];
    const groupCount = Math.floor(all.length / state.count);
    startProgress("正在生成批量修图包", groupCount);
    const zip = new JSZip();
    state.generatedBatch = [];
    for (let index = 0; index < groupCount; index += 1) {
      const group = all.slice(index * state.count, (index + 1) * state.count);
      const task = await createTaskPackage(group, `-G${String(index + 1).padStart(2, "0")}`);
      state.generatedBatch.push(task);
      updateProgress(index + 1, groupCount, `第 ${index + 1} / ${groupCount} 组`);
      const folder = zip.folder(`第${String(index + 1).padStart(2, "0")}组`);
      folder.file(task.imageName, task.imageBlob);
      folder.file(task.manifestName, task.manifestBlob);
      await saveTask(task.manifest);
      localStorage.setItem(`pixel-cake-stitcher:${task.manifest.taskId}`, JSON.stringify(task.manifest));
    }
    zip.file("使用说明.txt", `共 ${groupCount} 组。合成图已自带还原凭证，修图后直接上传合成图即可。像素蛋糕导出时请保持总分辨率并保留底部凭证区。JSON 仅作备用。\n`);
    startProgress("正在压缩批量修图包", 100);
    const blob = await zip.generateAsync({ type: "blob", compression: "DEFLATE", compressionOptions: { level: 6 } }, (metadata) => updateProgress(Math.round(metadata.percent), 100, metadata.currentFile || "整理压缩包"));
    downloadBlob(blob, `pc-${new Date().toISOString().slice(0, 10)}-${groupCount}组批量修图包.zip`);
    render();
  } catch (error) {
    alert(error.message || "批量生成失败，可能是图片尺寸或设备内存超过限制。");
  } finally {
    setBusy(false);
    finishProgress();
    render();
  }
}

async function downloadAllComposites() {
  const zip = new JSZip();
  state.generatedBatch.forEach((task, index) => {
    const folder = zip.folder(`第${String(index + 1).padStart(2, "0")}组`);
    folder.file(task.imageName, task.imageBlob);
    folder.file(task.manifestName, task.manifestBlob);
  });
  const blob = await zip.generateAsync({ type: "blob", compression: "DEFLATE", compressionOptions: { level: 6 } });
  downloadBlob(blob, `pc-${new Date().toISOString().slice(0, 10)}-全部合成图.zip`);
}

async function downloadFullPackage() {
  setBusy(true);
  startProgress("正在压缩完整修图包", 100);
  try {
    const zip = new JSZip();
    zip.file(state.generated.imageName, state.generated.imageBlob);
    zip.file(state.generated.manifestName, state.generated.manifestBlob);
    zip.file("使用说明.txt", "1. 将合成图导入像素蛋糕进行修图。\n2. 导出时保持总分辨率不变，不要裁剪底部凭证区。\n3. 回到本工具的“还原单张”，只上传修完的合成图。\n4. 工具会从图内读取凭证并下载拆分后的 ZIP。JSON 仅在凭证区损坏时备用。\n");
    const blob = await zip.generateAsync({ type: "blob", compression: "DEFLATE", compressionOptions: { level: 6 } }, (metadata) => updateProgress(Math.round(metadata.percent), 100, metadata.currentFile || "整理压缩包"));
    downloadBlob(blob, `${state.generated.manifest.taskId}-修图包.zip`);
  } finally {
    setBusy(false);
    finishProgress();
    render();
  }
}

function validateRestore() {
  if (!state.restoreImage || !state.manifest) return { ready: false, mismatch: false, html: "" };
  const exact = state.restoreImage.width === state.manifest.canvas.width && state.restoreImage.height === state.manifest.canvas.height;
  if (exact) return { ready: true, mismatch: false, html: `<div class="success">尺寸检查通过，可以按原始像素坐标准确拆分。</div>` };
  const proportional = Math.abs(state.restoreImage.width / state.manifest.canvas.width - state.restoreImage.height / state.manifest.canvas.height) < 0.0005;
  const detail = `当前 ${state.restoreImage.width} × ${state.restoreImage.height}，凭证要求 ${state.manifest.canvas.width} × ${state.manifest.canvas.height}。`;
  if (!proportional) return { ready: false, mismatch: true, html: `<div class="error">尺寸和宽高比例都已改变，无法可靠还原。${detail}</div>` };
  return {
    ready: state.allowResize,
    mismatch: true,
    html: `<div class="warning">合成图被等比例缩放。${detail} 可应急拆分并恢复目标尺寸，但会发生重新采样。</div>`,
  };
}

async function restoreImages() {
  if (state.restoreItems.length) return restoreBatchImages();
  const check = validateRestore();
  if (!check.ready) return;
  setBusy(true);
  startProgress("正在按原始坐标拆分照片", state.manifest.images.length);
  try {
    state.restoreResults = [await restoreOne({ image: state.restoreImage, manifest: state.manifest })];
    const zip = new JSZip();
    state.restoreResults[0].files.forEach((file) => zip.file(file.name, file.blob));
    zip.file("还原报告.txt", `任务：${state.manifest.taskId || "未知"}\n照片数量：${state.manifest.images.length}\n导出格式：PNG\n`);
    const blob = await zip.generateAsync({ type: "blob", compression: "DEFLATE", compressionOptions: { level: 6 } });
    downloadBlob(blob, `${state.manifest.taskId || "照片"}-还原单张.zip`);
  } catch (error) {
    alert(error.message || "还原失败。图片可能超过了当前浏览器的内存限制。");
  } finally {
    setBusy(false);
    finishProgress();
    render();
  }
}

function validateRestoreItem(item) {
  if (!item?.manifest) return { ready: false, message: "未识别凭证" };
  const exact = item.image.width === item.manifest.canvas.width && item.image.height === item.manifest.canvas.height;
  if (exact) return { ready: true, message: "尺寸检查通过" };
  const proportional = Math.abs(item.image.width / item.manifest.canvas.width - item.image.height / item.manifest.canvas.height) < 0.0005;
  return { ready: proportional && state.allowResize, message: proportional ? "等比例缩放，需允许应急还原" : "尺寸比例不匹配" };
}

async function restoreOne(item) {
  const exact = item.image.width === item.manifest.canvas.width && item.image.height === item.manifest.canvas.height;
  const files = [];
  for (const [index, image] of item.manifest.images.entries()) {
    const crop = exact ? image : scaledCrop(image, item.image.width, item.image.height, item.manifest.canvas.width, item.manifest.canvas.height);
    const canvas = createCanvas(image.width, image.height);
    const context = canvas.getContext("2d", { alpha: false });
    context.imageSmoothingEnabled = !exact;
    context.imageSmoothingQuality = "high";
    context.drawImage(item.image.bitmap, crop.x, crop.y, crop.width, crop.height, 0, 0, image.width, image.height);
    files.push({ name: image.outputName || outputName(image.originalName, image.index), blob: await canvasBlob(canvas, "image/png") });
    updateProgress(index + 1, item.manifest.images.length, image.originalName);
  }
  return { taskId: item.manifest.taskId || item.image.name, files };
}

async function restoreBatchImages() {
  const valid = state.restoreItems.filter((item) => validateRestoreItem(item).ready);
  setBusy(true);
  startProgress("正在批量还原合成图", valid.length);
  try {
    state.restoreResults = [];
    for (const [index, item] of valid.entries()) {
      const result = await restoreOne(item);
      state.restoreResults.push(result);
      updateProgress(index + 1, valid.length, item.image.name);
    }
  } catch (error) {
    alert(error.message || "批量还原失败。");
  } finally {
    setBusy(false);
    finishProgress();
    render();
  }
}

async function downloadAllRestored() {
  const zip = new JSZip();
  state.restoreResults.forEach((result) => result.files.forEach((file) => zip.file(`${result.taskId}/${file.name}`, file.blob)));
  zip.file("还原报告.txt", `共还原 ${state.restoreResults.length} 张合成图，结果已按任务分文件夹保存。\n`);
  const blob = await zip.generateAsync({ type: "blob", compression: "DEFLATE", compressionOptions: { level: 6 } });
  downloadBlob(blob, "照片-批量还原.zip");
}

function createCanvas(width, height) {
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  if (canvas.width !== width || canvas.height !== height || !canvas.getContext("2d")) throw new Error("浏览器无法创建此尺寸的画布。");
  return canvas;
}

function canvasBlob(canvas, type) {
  return new Promise((resolve, reject) => canvas.toBlob((blob) => blob ? resolve(blob) : reject(new Error("图片编码失败。")), type));
}

function outputName(name, index) {
  const base = name.replace(/\.[^.]+$/, "") || `照片-${index + 1}`;
  return `${String(index + 1).padStart(2, "0")}-${base}-修图.png`;
}

function downloadBlob(blob, name) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = name;
  link.click();
  setTimeout(() => URL.revokeObjectURL(url), 30000);
}

function moveImage(index, direction) {
  const target = index + direction;
  if (target < 0 || target >= state.images.length) return;
  [state.images[index], state.images[target]] = [state.images[target], state.images[index]];
  state.generated = null;
  state.generatedBatch = [];
  render();
}

function bindNumber(id, callback) {
  const input = workspace.querySelector(`#${id}`);
  input?.addEventListener("change", () => callback(Number(input.value)));
}

function clearCompose() {
  [...state.images, ...state.queue].forEach(cleanupImage);
  state.images = [];
  state.queue = [];
  state.generated = null;
  state.importStatus = "";
  render();
}

function clearRestore() {
  cleanupImage(state.restoreImage);
  state.restoreImage = null;
  state.manifest = null;
  state.restoreItems.forEach((item) => cleanupImage(item.image));
  state.restoreItems = [];
  state.restoreResults = [];
  state.allowResize = false;
  render();
}

function cleanupImage(image) {
  if (!image) return;
  image.bitmap?.close?.();
  if (image.url) URL.revokeObjectURL(image.url);
}

function setBusy(busy) {
  workspace.classList.toggle("busy", busy);
}

function startProgress(label, total) {
  state.progress = { label, current: 0, total: Math.max(1, total), detail: "准备中" };
  renderProgress();
}

function updateProgress(current, total, detail) {
  state.progress = { ...state.progress, current, total: Math.max(1, total), detail };
  renderProgress();
}

function finishProgress() {
  state.progress = null;
  workspace.querySelector("#work-progress")?.remove();
}

function renderProgress() {
  if (!state.progress) return;
  const { label, current, total, detail } = state.progress;
  const percent = Math.min(100, Math.round((current / total) * 100));
  let panel = workspace.querySelector("#work-progress");
  if (!panel) {
    workspace.insertAdjacentHTML("afterbegin", `<div id="work-progress" class="work-progress" role="status" aria-live="polite"><div class="progress-head"><strong></strong><span></span></div><div class="progress-track"><div class="progress-value"></div></div><small></small></div>`);
    panel = workspace.querySelector("#work-progress");
  }
  panel.querySelector("strong").textContent = label;
  panel.querySelector(".progress-head span").textContent = `${current} / ${total} · ${percent}%`;
  panel.querySelector(".progress-value").style.width = `${percent}%`;
  panel.querySelector("small").textContent = detail || "处理中";
}

function clamp(value, min, max) {
  return Math.max(min, Math.min(max, Number.isFinite(value) ? value : min));
}

function escapeHtml(value) {
  return String(value).replace(/[&<>"]/g, (character) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[character]);
}

render();
